import { Link } from "react-router-dom";
import type { Finding, ReportDetail } from "../api";
import { Time } from "../Time";

/**
 * What the latest report changed: findings it raised for the first time,
 * and lowlights it carried forward from an earlier one.
 *
 * "New" is `carried_from` being empty -- the same test `/api/findings`
 * uses, so this page and the cross-customer one cannot disagree. There is
 * no "resolved" column: a lowlight that stopped being repeated is an
 * absence, and nothing yet records it as a fact.
 */
export default function SinceReport({ customer, report }: { customer: string; report: ReportDetail }) {
  const r = report.report;
  const fresh = [
    ...r.lowlights.filter((f) => !f.carried_from).map((f) => ({ f, kind: "lowlight" as const })),
    ...r.highlights.filter((f) => !f.carried_from).map((f) => ({ f, kind: "highlight" as const })),
  ];
  const stuck = r.lowlights
    .filter((f) => f.carried_from)
    .sort((a, b) => (a.first_seen ?? "").localeCompare(b.first_seen ?? ""));

  return (
    <section className="ov-section" aria-labelledby="ov-since">
      <div className="ov-head">
        <h2 id="ov-since">
          Latest report ·{" "}
          <span className="dim">
            <Time value={report.period_start} mode="day" /> → <Time value={report.period_end} mode="day" />
          </span>
        </h2>
        <Link to={`/c/${customer}/report?r=${report.id}`}>Read full report</Link>
      </div>
      {r.executive_summary && <p className="ov-summary">{r.executive_summary}</p>}
      <div className="ov-since">
        <div className="card">
          <div className="ov-since-head">
            <span className="badge neg">New</span>
            <span className="dim">{fresh.length}</span>
          </div>
          {fresh.length === 0 ? (
            <p className="dim">Nothing raised for the first time.</p>
          ) : (
            fresh.map(({ f, kind }, i) => <Item key={f.id ?? `${kind}-${i}`} f={f} kind={kind} />)
          )}
        </div>
        <div className="card">
          <div className="ov-since-head">
            <span className="badge mix">Still open</span>
            <span className="dim">{stuck.length}</span>
          </div>
          {stuck.length === 0 ? (
            <p className="dim">No lowlight carried over from an earlier report.</p>
          ) : (
            stuck.map((f, i) => <Item key={f.id ?? i} f={f} since />)
          )}
        </div>
      </div>
    </section>
  );
}

function Item({ f, kind, since }: { f: Finding; kind?: "highlight" | "lowlight"; since?: boolean }) {
  const meta = [
    kind === "highlight" ? "Highlight" : kind === "lowlight" ? "Lowlight" : null,
    f.area,
    // A calendar date: shown as written, never put through a timezone.
    since && f.first_seen ? `since ${f.first_seen}` : null,
  ].filter(Boolean);
  return (
    <div className="ov-since-item">
      <strong>{f.title}</strong>
      {meta.length > 0 && <span className="dim">{meta.join(" · ")}</span>}
    </div>
  );
}
